import type { Edge, Node } from 'reactflow';

import { BotModuleData, StartBotModuleData } from '@/domain/bot';
import { BotMappieDialog, BotMappieUiConfig } from '@/domain/bot-mappie';
import { createSimpleEdgeReactFlow } from 'utils/reactflow/edges/create-simple-edge';
import { createNodeReactFlow } from 'utils/reactflow/nodes/create-node';

/**
 * Creates edges for handlers based on their key pattern
 * @param handlers - Object containing handler mappings
 * @param moduleId - ID of the current module
 * @param edgeDataModules - Array to push created edges into
 * @param pattern - Optional pattern to filter handler keys (defaults to 'on_')
 * @param handleIdExtractor - Optional function to extract handle ID from key
 */
const createEdgesBasedOnHandlers = (
    handlers: Record<string, any>,
    moduleId: string,
    edgeDataModules: Edge<any>[],
    pattern: string = 'on_',
) => {
    Object.keys(handlers).forEach((key) => {
        if (key.startsWith(pattern)) {
            let handlePort;
            handlePort = key.substring(pattern.length) || '0';

            if (handlers[key] && handlers[key] !== '') {
                edgeDataModules.push(
                    createSimpleEdgeReactFlow(
                        moduleId,
                        handlers[key],
                        handlePort,
                    ),
                );
            }
        }
    });
};

export const modulesToNodesReactFlow = (
    botMappieDialog?: BotMappieDialog,
    botMappieUi?: BotMappieUiConfig,
) => {
    const nodeDataModules: Node<BotModuleData>[] = [];

    if (botMappieDialog) {
        for (const [moduleId, mod] of Object.entries(botMappieDialog.modules)) {
            const position = botMappieUi?.module_positions?.[moduleId];

            const createdNode = createNodeReactFlow(
                moduleId,
                mod.type,
                (mod as any).params,
                (mod as any).source,
                position?.x,
                position?.y,
            );

            nodeDataModules.push(createdNode);
        }

        nodeDataModules.unshift({
            id: 'start',
            type: 'StartNode',
            data: {
                type: 'Start',
            } as StartBotModuleData,
            position: botMappieUi?.module_positions?.start ?? { x: 0, y: 0 },
        });
    }

    return nodeDataModules;
};

export const modulesToEdgesReactFlow = (botMappieDialog?: BotMappieDialog) => {
    //TODO add type of EdgeData data instead of any
    const edgeDataModules: Edge<any>[] = [];

    if (botMappieDialog) {
        const objectEntriesModulesMappie = Object.entries(
            botMappieDialog.modules,
        );

        for (const [moduleId, mod] of objectEntriesModulesMappie) {
            const handlers = (mod as any).handlers;
            const nodeExit = handlers?.node_exit;

            if (mod.type === 'YesNoConfirmation') {
                const nodeHandleNo = handlers?.on_no;
                const nodeFallback = handlers?.on_fallback;
                const nodeEscape = handlers?.on_escape;

                if (nodeExit) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeExit, 'Y'),
                    );
                }
                if (nodeHandleNo) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeHandleNo, 'N'),
                    );
                }
                if (nodeFallback) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeFallback, 'F'),
                    );
                }
                if (nodeEscape) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeEscape, 'S'),
                    );
                }
            } else if (mod.type === 'ValueRangeRouter') {
                const onNotSet = handlers?.on_value_not_set;
                const onOutOfBounds = handlers?.on_value_out_of_bounds;

                createEdgesBasedOnHandlers(
                    handlers,
                    moduleId,
                    edgeDataModules,
                    'on_value_in_range_',
                );

                if (onNotSet) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, onNotSet, 'N'),
                    );
                }
                if (onOutOfBounds) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, onOutOfBounds, 'O'),
                    );
                }
            } else if (moduleId.startsWith('custom_internal_module')) {
                createEdgesBasedOnHandlers(handlers, moduleId, edgeDataModules);
            } else if (mod.type === 'GPTModule') {
                const nodeOnException = handlers?.on_exception;
                const nodeOnContentFilterException =
                    handlers?.on_content_filter_exception;
                const nodeTimeoutException = handlers?.on_timeout_exception;
                const nodeExit = handlers?.node_exit;

                if (nodeExit) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeExit, 'E'),
                    );
                }
                if (nodeOnException) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            nodeOnException,
                            'C',
                        ),
                    );
                }
                if (nodeOnContentFilterException) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            nodeOnContentFilterException,
                            'L',
                        ),
                    );
                }
                if (nodeTimeoutException) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            nodeTimeoutException,
                            'M',
                        ),
                    );
                }
            } else if (mod.type === 'MSearchModule') {
                const nodeError = handlers?.on_error;
                const nodeNoResultFound = handlers?.on_no_result_found;
                const nodeExit = handlers?.node_exit;

                if (nodeExit) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeExit, 'E'),
                    );
                }
                if (nodeError) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeError, 'C'),
                    );
                }
                if (nodeNoResultFound) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            nodeNoResultFound,
                            'L',
                        ),
                    );
                }
            } else if (mod.type === 'TransferToAgentModule') {
                const onAgentNotAvailable = handlers?.on_agent_not_available;
                const onMsgReceived = handlers?.on_message_received;
                const onMessageDenied = handlers?.on_message_denied;

                if (onAgentNotAvailable) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            onAgentNotAvailable,
                            'A',
                        ),
                    );
                }
                if (onMsgReceived) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, onMsgReceived, 'R'),
                    );
                }
                if (onMessageDenied) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(
                            moduleId,
                            onMessageDenied,
                            'D',
                        ),
                    );
                }
            } else if (mod.type === 'NumericRatingModule') {
                const onFallback = handlers?.on_fallback;
                const nodeExit = handlers?.node_exit;

                if (nodeExit) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeExit, 'E'),
                    );
                }
                if (onFallback) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, onFallback, 'F'),
                    );
                }
            } else {
                if (nodeExit) {
                    edgeDataModules.push(
                        createSimpleEdgeReactFlow(moduleId, nodeExit, 'E'),
                    );
                }
            }
        }

        if (botMappieDialog.root_module) {
            edgeDataModules.push(
                createSimpleEdgeReactFlow(
                    'start',
                    botMappieDialog.root_module,
                    'E',
                ),
            );
        }
    }
    // in case the end module is not present in modules the bot was not created as a graph and it is necessary to add link from last node to end
    // if (
    //     !objectEntriesModulesMappie.some(
    //         ([moduleId, mod]) => moduleId === 'end' || mod.type === 'Exit',
    //     ) &&
    //     objectEntriesModulesMappie.length > 0
    // ) {
    //     const [lastModuleId, lastMod] =
    //         objectEntriesModulesMappie[objectEntriesModulesMappie.length - 1];

    //     if (lastMod?.type === 'YesNoConfirmation') {
    //         edgeDataModules.push(createSimpleEdge(lastModuleId, 'end', 'Y'));
    //         edgeDataModules.push(createSimpleEdge(lastModuleId, 'end', 'N'));
    //     } else {
    //         edgeDataModules.push(createSimpleEdge(lastModuleId, 'end', 'E'));
    //     }
    // }

    return edgeDataModules;
};
